import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  RollbackOutlined,
} from '@ant-design/icons'
import { Button, Modal, Popconfirm, Space, Tooltip, Typography } from 'antd'
import { includes, isEmpty, isNil, map, pick, size } from 'lodash'
import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'

import { ALLOWED_METADATA_TREE_OPERATIONS } from '../../constants'
import { nodePropType } from '../../prop-types'
import { isDbId, isNodeOnLastLevel } from '../../utils'
import { StyledVerticalSpace } from '../styled'

const MetadataNodeTitleButtons = ({
  node,
  editable,
  addNode,
  deleteNode,
  renameNode,
  setDataForRenaming,
  setIsCurationEnabled,
  revertReplaceNode,
  isTreeDirty,
  isRenameInputShown,
}) => {
  const [isRevertModalVisible, setIsRevertModalVisible] = useState(false)
  const isCurationInitiallyEnabled = useRef(node.isCurationEnabled)

  const isRenameButtonShown = useMemo(
    () =>
      editable &&
      !isNil(renameNode) &&
      includes(node.allowedOperations, ALLOWED_METADATA_TREE_OPERATIONS.RENAME),
    [editable, node.allowedOperations, renameNode],
  )

  const isAddChildButtonShown = useMemo(
    () =>
      editable &&
      !isNodeOnLastLevel(node) &&
      !isNil(addNode) &&
      includes(
        node.allowedOperations,
        ALLOWED_METADATA_TREE_OPERATIONS.ADD_CHILD,
      ),
    [addNode, editable, node],
  )
  const isSetIsCurationEnabledNodeEnabled = useMemo(
    () =>
      isNil(node.replaceToken) &&
      editable &&
      !isTreeDirty &&
      isDbId(node.id) &&
      !isNil(setIsCurationEnabled) &&
      includes(
        node.allowedOperations,
        ALLOWED_METADATA_TREE_OPERATIONS.SET_IS_CURATION_ENABLED,
      ),
    [
      editable,
      isTreeDirty,
      node.allowedOperations,
      node.id,
      node.replaceToken,
      setIsCurationEnabled,
    ],
  )

  const isDeleteButtonShown = useMemo(
    () =>
      isEmpty(node.children) &&
      isDbId(node.id) &&
      !isTreeDirty &&
      editable &&
      ((!node.isCurationEnabled &&
        (!isCurationInitiallyEnabled.current ||
          !isSetIsCurationEnabledNodeEnabled)) ||
        includes(
          node.allowedOperations,
          ALLOWED_METADATA_TREE_OPERATIONS.SKIP_SET_IS_CURATION_ENABLED,
        )) &&
      !isNil(deleteNode) &&
      includes(
        node.allowedOperations,
        ALLOWED_METADATA_TREE_OPERATIONS.DELETE,
      ) &&
      isEmpty(node.replaces),
    [
      deleteNode,
      editable,
      isSetIsCurationEnabledNodeEnabled,
      isTreeDirty,
      node.allowedOperations,
      node.children,
      node.id,
      node.isCurationEnabled,
      node.replaces,
    ],
  )

  const isRevertReplaceButtonShown = useMemo(
    () =>
      (!isEmpty(node.replaces) || !isEmpty(node.replacedBy)) &&
      !isTreeDirty &&
      editable &&
      !isNil(revertReplaceNode) &&
      includes(
        node.allowedOperations,
        ALLOWED_METADATA_TREE_OPERATIONS.REVERT_REPLACE,
      ),
    [
      node.replaces,
      node.replacedBy,
      node.allowedOperations,
      isTreeDirty,
      editable,
      revertReplaceNode,
    ],
  )

  const handleAddChildNode = useCallback(() => {
    const indexToAddNodeAt = size(node.children)

    const newNode = {
      id: nanoid(),
      name: '',
    }

    addNode({
      ...newNode,
      key: `${node.key}-${indexToAddNodeAt}`,
      path: `${node.path}.children[${indexToAddNodeAt}]`,
      keyPath: [...node.keyPath, `${node.key}-${indexToAddNodeAt}`],
      parentPath: node.path,
      parentId: node.id || null,
      isCurationEnabled: true,
      level: node.level + 1,
      idPath: [...node.idPath, newNode.id],
      namePath: [...node.namePath, newNode.name],
      allowedOperations: node.allowedOperations,
    })
  }, [addNode, node])

  const handleDelete = useCallback(
    () => deleteNode(pick(node, ['id', 'parentPath', 'type'])),
    [deleteNode, node],
  )

  const handleRevertReplace = useCallback(
    replaceToken => revertReplaceNode({ replaceToken }),
    [revertReplaceNode],
  )

  const handleSetIsRenamingNode = useCallback(() => {
    setDataForRenaming(isRenameInputShown ? {} : pick(node, ['id', 'type']))
  }, [setDataForRenaming, isRenameInputShown, node])

  const handleSetIsCurationEnabled = useCallback(
    () =>
      setIsCurationEnabled({
        ...pick(node, ['id', 'path', 'type']),
        isCurationEnabled: !node.isCurationEnabled,
      }),
    [node, setIsCurationEnabled],
  )

  const handleCloseRevertModal = useCallback(() => {
    setIsRevertModalVisible(false)
  }, [])

  const handleOpenRevertModal = useCallback(() => {
    setIsRevertModalVisible(true)
  }, [])

  useEffect(() => {
    if (
      !isTreeDirty &&
      node.isCurationEnabled !== isCurationInitiallyEnabled.current
    ) {
      isCurationInitiallyEnabled.current = node.isCurationEnabled
    }
  }, [isTreeDirty, node.isCurationEnabled])

  return (
    <Space>
      {isRenameButtonShown && (
        <Tooltip title="Edit name">
          <Button
            onClick={handleSetIsRenamingNode}
            size="small"
            shape="circle"
            icon={<EditOutlined />}
          />
        </Tooltip>
      )}
      {isAddChildButtonShown && (
        <Tooltip title="Add Child Node">
          <Button
            size="small"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={handleAddChildNode}
          />
        </Tooltip>
      )}
      {isDeleteButtonShown && (
        <Popconfirm
          title="Are you sure you want to delete?"
          onConfirm={handleDelete}
        >
          <Button
            size="small"
            danger
            shape="circle"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      )}
      {isRevertReplaceButtonShown && (
        <>
          <Button
            size="small"
            danger
            shape="circle"
            onClick={handleOpenRevertModal}
            icon={<RollbackOutlined />}
          />
          {isRevertModalVisible && (
            <Modal
              visible
              title={`Replaced/Replaces for ${node.name}`}
              footer={null}
              onCancel={handleCloseRevertModal}
            >
              <>
                {!isEmpty(node.replacedBy) && (
                  <StyledVerticalSpace>
                    <Typography.Text>Replaced by:</Typography.Text>
                    {map(node.replacedBy, ({ id, name, replaceToken }) => (
                      <Space key={id}>
                        <Typography.Text>{name}</Typography.Text>

                        <Popconfirm
                          title="Are you sure you want to revert replacement?"
                          onConfirm={() => handleRevertReplace(replaceToken)}
                        >
                          <Button
                            size="small"
                            danger
                            shape="circle"
                            icon={<RollbackOutlined />}
                          />
                        </Popconfirm>
                      </Space>
                    ))}
                  </StyledVerticalSpace>
                )}

                {!isEmpty(node.replaces) && (
                  <StyledVerticalSpace>
                    <Typography.Text>Replaces:</Typography.Text>
                    {map(node.replaces, ({ id, name, replaceToken }) => (
                      <Space key={id}>
                        <Typography.Text>{name}</Typography.Text>

                        <Popconfirm
                          title="Are you sure you want to revert replacement?"
                          onConfirm={() => handleRevertReplace(replaceToken)}
                        >
                          <Button
                            size="small"
                            danger
                            shape="circle"
                            icon={<RollbackOutlined />}
                          />
                        </Popconfirm>
                      </Space>
                    ))}
                  </StyledVerticalSpace>
                )}
              </>
            </Modal>
          )}
        </>
      )}

      <Popconfirm
        title={
          node.isCurationEnabled
            ? 'Are you sure you want to mark this as unavailable for curation?'
            : 'Are you sure you want to make this available for curation?'
        }
        onConfirm={handleSetIsCurationEnabled}
        disabled={!isSetIsCurationEnabledNodeEnabled}
      >
        <Tooltip title="Disable Curation">
          <Button
            size="small"
            danger={!node.isCurationEnabled}
            disabled={!isSetIsCurationEnabledNodeEnabled}
            shape="circle"
            icon={
              node.isCurationEnabled ? (
                <EyeOutlined />
              ) : (
                <EyeInvisibleOutlined />
              )
            }
          />
        </Tooltip>
      </Popconfirm>
    </Space>
  )
}

MetadataNodeTitleButtons.propTypes = {
  node: nodePropType.isRequired,
  editable: PropTypes.bool,
  addNode: PropTypes.func,
  deleteNode: PropTypes.func,
  renameNode: PropTypes.func,
  setDataForRenaming: PropTypes.func,
  setIsCurationEnabled: PropTypes.func,
  revertReplaceNode: PropTypes.func,
  isRenameInputShown: PropTypes.bool,
  isTreeDirty: PropTypes.bool,
}

MetadataNodeTitleButtons.defaultProps = {
  editable: false,
  addNode: undefined,
  deleteNode: undefined,
  renameNode: undefined,
  setDataForRenaming: undefined,
  setIsCurationEnabled: undefined,
  revertReplaceNode: undefined,
  isRenameInputShown: false,
  isTreeDirty: false,
}

export default MetadataNodeTitleButtons
